import React, { useEffect, useState } from 'react';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, LayoutGrid, List, Upload, X, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { SearchableOwnerSelect } from '@/components/SearchableOwnerSelect';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { compressImage, validateImageFile } from '@/utils/imageCompression';
import { uploadClinicImage } from '@/utils/cloudinary';

export const Diagnoses = () => {
  const [diagnoses, setDiagnoses] = useState([]);
  const [owners, setOwners] = useState([]);
  const [pets, setPets] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState('card');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    owner_id: '',
    pet_id: '',
    doctor_id: '',
    diagnosis_details: '',
    pet_weight: '',
    visit_date: new Date().toISOString().split('T')[0],
    image_url: '',
  });

  useEffect(() => {
    fetchDiagnoses();
    fetchOwners();
    fetchPets();
    fetchDoctors();
  }, []);

  const fetchDiagnoses = async () => {
    try {
      const response = await api.get('/diagnoses');
      setDiagnoses(response.data);
    } catch (error) {
      toast.error('Failed to load service records');
    }
  };

  const fetchOwners = async () => {
    try {
      const response = await api.get('/owners');
      setOwners(response.data);
    } catch (error) {
      toast.error('Failed to load owners');
    }
  };

  const fetchPets = async () => {
    try {
      const response = await api.get('/pets');
      setPets(response.data);
    } catch (error) {
      toast.error('Failed to load pets');
    }
  };

  const fetchDoctors = async () => {
    try {
      const response = await api.get('/doctors');
      setDoctors(response.data);
    } catch (error) {
      toast.error('Failed to load doctors');
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const validation = validateImageFile(file, 3);
    if (!validation.isValid) {
      toast.error(validation.error);
      return;
    }

    try {
      // Compress image
      toast.info('Compressing image...');
      const compressedFile = await compressImage(file, {
        maxSizeMB: 3,
        maxWidthOrHeight: 1920,
        quality: 0.8
      });

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(compressedFile);

      setImageFile(compressedFile);
      toast.success(`Image compressed to ${(compressedFile.size / (1024 * 1024)).toFixed(2)}MB`);
    } catch (error) {
      toast.error('Failed to process image: ' + error.message);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setFormData({ ...formData, image_url: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      let imageUrl = formData.image_url;

      // Upload image to Cloudinary if a new image was selected
      if (imageFile) {
        toast.info('Uploading image...');
        imageUrl = await uploadClinicImage(imageFile, 'diagnoses');
        toast.success('Image uploaded successfully');
      }

      // Submit diagnosis with image URL
      await api.post('/diagnoses', {
        ...formData,
        pet_weight: parseFloat(formData.pet_weight),
        image_url: imageUrl
      });

      toast.success('Service record added successfully');
      setIsDialogOpen(false);
      setFormData({
        owner_id: '',
        pet_id: '',
        doctor_id: '',
        diagnosis_details: '',
        pet_weight: '',
        visit_date: new Date().toISOString().split('T')[0],
        image_url: '',
      });
      setImageFile(null);
      setImagePreview(null);
      fetchDiagnoses();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Operation failed');
    } finally {
      setUploading(false);
    }
  };

  const getOwnerName = (ownerId) => owners.find((o) => o.id === ownerId)?.full_name || 'Unknown';
  const getPetName = (petId) => pets.find((p) => p.id === petId)?.name || 'Unknown';
  const getDoctorName = (doctorId) => doctors.find((d) => d.id === doctorId)?.full_name || 'Unknown';

  // Filter pets based on selected owner
  const filteredPets = formData.owner_id 
    ? pets.filter((pet) => pet.owner_id === formData.owner_id)
    : [];

  return (
    <div className="p-6 md:p-12" data-testid="diagnoses-page">
      <div className="mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-sage-800 mb-2">Service Records</h1>
          <p className="text-gray-600">Record and track pet service history</p>
        </div>
        <div className="flex gap-3">
          <div className="flex bg-white rounded-full shadow border border-stone-200">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('card')}
              className={`rounded-full ${viewMode === 'card' ? 'bg-sage-500 text-white hover:bg-sage-600 hover:text-white' : ''}`}
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('table')}
              className={`rounded-full ${viewMode === 'table' ? 'bg-sage-500 text-white hover:bg-sage-600 hover:text-white' : ''}`}
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-sage-500 hover:bg-sage-600 text-white rounded-full shadow-lg"
              data-testid="add-diagnosis-button"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Service Record
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Record New Service</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="diagnosis-form">
              <div>
                <Label htmlFor="owner_id">Owner</Label>
                <SearchableOwnerSelect
                  owners={owners}
                  value={formData.owner_id}
                  onValueChange={(value) => setFormData({ ...formData, owner_id: value, pet_id: '' })}
                  placeholder="Select owner"
                  testId="diagnosis-owner-select"
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="pet_id">Pet</Label>
                <Select value={formData.pet_id} onValueChange={(value) => setFormData({ ...formData, pet_id: value })} disabled={!formData.owner_id}>
                  <SelectTrigger className="rounded-xl mt-2" data-testid="diagnosis-pet-select">
                    <SelectValue placeholder={formData.owner_id ? "Select pet" : "Select owner first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredPets.map((pet) => (
                      <SelectItem key={pet.id} value={pet.id}>
                        {pet.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="doctor_id">Doctor</Label>
                <Select value={formData.doctor_id} onValueChange={(value) => setFormData({ ...formData, doctor_id: value })}>
                  <SelectTrigger className="rounded-xl mt-2" data-testid="diagnosis-doctor-select">
                    <SelectValue placeholder="Select doctor" />
                  </SelectTrigger>
                  <SelectContent>
                    {doctors.map((doctor) => (
                      <SelectItem key={doctor.id} value={doctor.id}>
                        {doctor.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="pet_weight">Pet Weight (kg)</Label>
                <Input
                  id="pet_weight"
                  type="number"
                  step="0.1"
                  value={formData.pet_weight}
                  onChange={(e) => setFormData({ ...formData, pet_weight: e.target.value })}
                  className="rounded-xl mt-2"
                  required
                  data-testid="diagnosis-weight-input"
                />
              </div>
              <div>
                <Label htmlFor="visit_date">Visit Date</Label>
                <Input
                  id="visit_date"
                  type="date"
                  value={formData.visit_date}
                  onChange={(e) => setFormData({ ...formData, visit_date: e.target.value })}
                  className="rounded-xl mt-2"
                  required
                  data-testid="diagnosis-date-input"
                />
              </div>
              <div>
                <Label>Service Details</Label>
                <Textarea
                  value={formData.diagnosis_details}
                  onChange={(e) => setFormData({ ...formData, diagnosis_details: e.target.value })}
                  className="mt-2 rounded-xl min-h-[200px]"
                  placeholder="Enter service details..."
                  required
                  data-testid="diagnosis-details-input"
                />
              </div>
              <div>
                <Label htmlFor="image">Image (Optional)</Label>
                <p className="text-xs text-gray-500 mt-1 mb-2">
                  Max 3MB. Image will be automatically compressed.
                </p>
                <div className="space-y-3">
                  {imagePreview ? (
                    <div className="relative">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-full h-48 object-cover rounded-xl border-2 border-sage-200"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        onClick={handleRemoveImage}
                        className="absolute top-2 right-2 rounded-full"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-sage-400 transition-colors">
                      <input
                        id="image"
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleImageChange}
                        className="hidden"
                        data-testid="diagnosis-image-input"
                      />
                      <label htmlFor="image" className="cursor-pointer">
                        <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm font-medium text-gray-700">
                          Click to upload image
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          JPG, PNG or WebP (max 3MB)
                        </p>
                      </label>
                    </div>
                  )}
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full rounded-full bg-sage-500 hover:bg-sage-600 text-white" 
                data-testid="diagnosis-submit-button"
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Record Service'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {diagnoses.length === 0 ? (
        <div className="text-center py-12 text-gray-500 bg-white rounded-3xl shadow-lg">
          No service records yet. Add your first service record to get started!
        </div>
      ) : viewMode === 'card' ? (
        <div className="space-y-6">
          {diagnoses.map((diagnosis, idx) => (
            <div
              key={diagnosis.id}
              className="bg-white rounded-3xl shadow-lg p-6 hover:shadow-2xl transition-all duration-300 mb-2"
              data-testid={`diagnosis-card-${idx}`}
            >
              <div className="flex flex-wrap gap-3 justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-heading font-bold text-sage-800">
                    {getPetName(diagnosis.pet_id)} - {getOwnerName(diagnosis.owner_id)}
                  </h3>
                  <p className="text-sm text-gray-600">
                    Dr. {getDoctorName(diagnosis.doctor_id)} • {new Date(diagnosis.visit_date).toLocaleDateString()}
                  </p>
                </div>
                <div className="bg-sage-100 px-4 py-2 rounded-full">
                  <span className="text-sm font-semibold text-sage-700">{diagnosis.pet_weight} kg</span>
                </div>
              </div>
              {diagnosis.image_url && (
                <div className="mb-4">
                  <img 
                    src={diagnosis.image_url} 
                    alt="Service record" 
                    className="w-full h-64 object-cover rounded-xl border border-sage-200"
                  />
                </div>
              )}
              <div
                className="text-gray-700 whitespace-pre-wrap"
              >
                {diagnosis.diagnosis_details}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-3xl shadow-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-bold text-sage-800">Pet</TableHead>
                <TableHead className="font-bold text-sage-800">Owner</TableHead>
                <TableHead className="font-bold text-sage-800">Doctor</TableHead>
                <TableHead className="font-bold text-sage-800">Visit Date</TableHead>
                <TableHead className="font-bold text-sage-800">Weight (kg)</TableHead>
                <TableHead className="font-bold text-sage-800">Details</TableHead>
                <TableHead className="font-bold text-sage-800">Image</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {diagnoses.map((diagnosis, idx) => (
                <TableRow key={diagnosis.id} data-testid={`diagnosis-row-${idx}`}>
                  <TableCell className="font-medium">{getPetName(diagnosis.pet_id)}</TableCell>
                  <TableCell>{getOwnerName(diagnosis.owner_id)}</TableCell>
                  <TableCell>Dr. {getDoctorName(diagnosis.doctor_id)}</TableCell>
                  <TableCell>{new Date(diagnosis.visit_date).toLocaleDateString()}</TableCell>
                  <TableCell className="font-semibold text-sage-700">{diagnosis.pet_weight}</TableCell>
                  <TableCell className="max-w-md truncate">{diagnosis.diagnosis_details}</TableCell>
                  <TableCell>
                    {diagnosis.image_url ? (
                      <a 
                        href={diagnosis.image_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sage-600 hover:text-sage-700"
                      >
                        <ImageIcon className="w-5 h-5" />
                      </a>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
